import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormItem, FormLabel } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { savedIndicatorSite, SiteIndicatorFields } from './SiteIndicatorFields';
import { Widget } from './Widget';

export function NewValueWidgetData ({ data, onChangeField, onCompleted }) {
    const
        decimalOptions = ['0', '1', '2', '3'],
        periodOptions = [
            { label: i18next.t('glossary.last_value').capitalize(), value: 'last_value' },
            { label: i18next.t('periods.verbose.wtd').capitalize(), value: 'wtd' },
            { label: i18next.t('periods.verbose.mtd').capitalize(), value: 'mtd' },
            { label: i18next.t('periods.verbose.ytd').capitalize(), value: 'ytd' }
        ],
        unitScaleOptions = [
            { label: i18next.t('unit_scales.auto'), value: -1 },
            { label: i18next.t('unit_scales.full'), value: 0 },
            { label: i18next.t('unit_scales.thousands'), value: 3 },
            { label: i18next.t('unit_scales.millions'), value: 6 },
            { label: i18next.t('unit_scales.billions'), value: 9 }
        ];

    const formSchema = z.object({
        decimals: z.string().min(1, i18next.t('form_validations.generic.required')),
        indicatorId: z.string().min(1, i18next.t('form_validations.generic.indicator_required')),
        period: z.string().min(1, i18next.t('form_validations.generic.required')),
        site: z.string().min(1, i18next.t('form_validations.generic.required')),
        unitScale: z.number().int(i18next.t('form_validations.generic.required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            decimals: data.data?.decimals || '2',
            indicatorId: data.data?.indicatorId || '',
            period: data.data?.period || '',
            site: savedIndicatorSite(data.data?.indicatorId),
            unitScale: data.data?.unitScale ?? -1
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = useRef({
        decimals: debounce(() => form.trigger('decimals'), 600),
        period: debounce(() => form.trigger('period'), 600),
        unitScale: debounce(() => form.trigger('unitScale'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <SiteIndicatorFields
                        data={data}
                        emptyLabel={i18next.t('indicators.no_indicators_found')}
                        filter={() => true}
                        form={form}
                        onChangeField={onChangeField}
                        onIndicatorChange={(_, indicatorId) => onChangeField('data')({ ...data.data, indicatorId })}
                    />
                    <FormField
                        control={form.control}
                        name="period"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('common.reference_period').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            const selected = periodOptions.find(p => p.value === val);

                                            onChangeField('data')({
                                                ...data.data,
                                                period: val
                                            });

                                            onChangeField('legend')(selected?.label || '');

                                            debouncedTriggers.current.unitScale();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('common.reference_period').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            { periodOptions.map(period => (
                                                <SelectItem key={period.value} value={period.value}>
                                                    {period.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="unitScale"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('indicatorKinds.power_factor').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            onChangeField('data')({
                                                ...data.data,
                                                unitScale: val
                                            });

                                            debouncedTriggers.current.unitScale();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('indicatorKinds.power_factor').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            { unitScaleOptions.map(unitScale => (
                                                <SelectItem key={unitScale.value} value={unitScale.value}>
                                                    {unitScale.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="decimals"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.decimals').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            onChangeField('data')({
                                                ...data.data,
                                                decimals: val
                                            });

                                            debouncedTriggers.current.decimals();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('glossary.decimals').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            { decimalOptions.map(decimal => (
                                                <SelectItem key={decimal} value={decimal}>
                                                    {decimal}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
